import { Link } from "react-router-dom";
import Cart from "svgs/cart";
import Header from "./Header";
import { IoSearch } from "react-icons/io5";
import { FiMenu } from "react-icons/fi";
import logo from "../../src/images/logo.webp";
import DrawerMenu from "components/dashboardComponents/drawerMenu";
import { useEffect, useMemo, useState } from "react";
import useAllProductsData from "hook/useAllProductsData";
import Lottie from "lottie-react";
import flameAnimation from "../json/FlameAnimation.json";
import {
  CART_STORAGE_KEY,
  CART_UPDATED_EVENT,
  getCartItemCount,
} from "utils/cartStorage";
const normalizeText = (text = "") =>
  text.toString().toLowerCase().replace(/\s+/g, "").trim();

const Navbar = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { allProducts } = useAllProductsData();
  const [cartItemCount, setCartItemCount] = useState(0);

  useEffect(() => {
    const updateCartItemCount = () => {
      setCartItemCount(getCartItemCount());
    };

    const handleStorage = (event) => {
      if (event.key === CART_STORAGE_KEY) {
        updateCartItemCount();
      }
    };

    updateCartItemCount();
    window.addEventListener(CART_UPDATED_EVENT, updateCartItemCount);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, updateCartItemCount);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  /* 🔹 Pre-flatten variants (optimized) */
  const variants = useMemo(() => {
    return allProducts.flatMap((product) =>
      product.variants.map((variant) => ({
        variant_id: variant.variant_id,
        title: variant.title,
        title_normalized: normalizeText(variant.title),
        price: variant.price,
        flashPrice: variant.flashPrice,
        item_id: product.item_id,
        catalogue: product.catalogue,
        image: variant.images?.length ? variant.images[0] : null,
      })),
    );
  }, [allProducts]);

  /* 🔹 Search ONLY by variant.title */
  const searchResults = useMemo(() => {
    if (!searchText.trim()) return [];

    const keyword = normalizeText(searchText);

    return variants
      .filter((v) => v.title_normalized.includes(keyword))
      .slice(0, 10);
  }, [searchText, variants]);

  return (
    <div className="sticky top-0 z-50 bg-white shadow-md">
      <Header />
      <nav className="">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center lg:py-2 py-1">
            {/* Mobile layout - shows below lg breakpoint */}
            <div className="flex md:hidden items-center w-full gap-2">
              {/* Sidebar icon on left (mobile only) */}

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={toggleDrawer}
                  className="p-2 text-gray-700 hover:text-gray-900 focus:outline-none"
                >
                  <FiMenu className="h-6 w-6" />
                </button>

                {/* Logo in center */}
                {/* <div className="flex items-center "> */}
                {/* <span className="text-lg font-bold text-green-600 whitespace-nowrap"> */}
                <Link to="/" className="flex ">
                  <img src={logo} alt="Noyabibi Logo" className="h-12 w-auto" />
                </Link>
                {/* </span> */}
                {/* </div> */}
              </div>

              {/* search */}
              <div className="flex-grow relative">
                <input
                  lang="bn"
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="পণ্য খুঁজুন"
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                />

                <button className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-500">
                  <IoSearch size={16} />
                </button>

                {/* Mobile Suggestions */}
                {searchText && (
                  <div className="absolute z-50 bg-white border w-full mt-1 rounded shadow max-h-72 overflow-y-auto">
                    {searchResults.length > 0 ? (
                      searchResults.map((item) => (
                        <Link
                          key={item.variant_id}
                          to={`/details/${item.catalogue}/${item.item_id}`}
                          onClick={() => setSearchText("")}
                          className="flex items-center gap-2 p-2 hover:bg-gray-100"
                        >
                          <img
                            src={`https://backend.nuhababy.com/${item.image}`}
                            className="w-8 h-8 rounded object-cover"
                            alt={item.title}
                          />
                          <div>
                            <p className="text-xs font-medium">{item.title}</p>
                            <p className="text-[10px] text-gray-500">
                              ৳ {item.flashPrice}
                            </p>
                          </div>
                        </Link>
                      ))
                    ) : (
                      <div className="p-3 text-center text-xs text-gray-500">
                        No product found
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Offer and Cart on right (mobile only) */}
              <div className="flex items-center gap-1 shrink-0" lang="bn">
                <Link to="/hotOffer">
                  <div className="w-9 h-9 rounded-md bg-red-300 overflow-hidden flex items-center justify-center">
                    <Lottie
                      animationData={flameAnimation}
                      loop
                      className="w-full h-full"
                    />
                  </div>
                </Link>
                <Link
                  to="/cart"
                  className="relative border border-[#fff]  rounded-md transition-colors outline-1 outline-gray-500 p-1 "
                >
                  <Cart />
                  {cartItemCount > 0 && (
                    <span className="absolute -right-2 -top-2 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold leading-5 text-center">
                      {cartItemCount > 99 ? "99+" : cartItemCount}
                    </span>
                  )}
                  <span className="sr-only">Cart</span>
                </Link>
              </div>
            </div>

            {/* Desktop layout - shows above lg breakpoint */}
            <div className="hidden lg:flex items-center w-full">
              <div className="flex items-center mr-5">
                <span className="text-lg font-bold text-green-600">
                  <Link to="/" className="flex items-center">
                    <img
                      src={logo}
                      alt="Noyabibi Logo"
                      className="h-12 w-auto"
                    />
                  </Link>
                </span>
              </div>
              {/* <div className="mr-5" lang="bn">
                <Link to="/hotOffer" className="">
                   <Lottie
    animationData={flameAnimation}
    loop={true}
    className="w-12 h-12"
  />
                </Link>
              </div> */}
              <div
                className="mr-5 w-12 h-12 rounded-md bg-red-300 overflow-hidden flex items-center justify-center"
                lang="bn"
              >
                <Link to="/hotOffer">
                  <Lottie
                    animationData={flameAnimation}
                    loop={true}
                    style={{ transform: "scale(1.2)" }}
                    className="w-full h-full"
                  />
                </Link>
              </div>
            </div>

            {/* <div className="hidden lg:flex items-center flex-grow">
              <input
                type="text"
                placeholder="Search for products"
                className="flex-grow border border-gray-300 rounded-l-md py-2 px-4 "
              />
              <button className="bg-red-500 text-white py-3 px-4 rounded-r-md hover:bg-red-600 transition">
                <IoSearch />
              </button>
            </div> */}

            <div className="hidden md:flex items-center w-full gap-6">
              {/* 🔍 Search Box */}
              <div className="relative flex-grow">
                <input
                  lang="bn"
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="পণ্য খুঁজুন"
                  className="w-full border border-gray-300 rounded-l-md px-4 py-2"
                />

                <button className="absolute right-0 top-0 h-full px-4 bg-red-500 text-white rounded-r-md">
                  <IoSearch />
                </button>

                {/* Suggestions */}
                {searchText && (
                  <div className="absolute z-50 bg-white border w-full mt-1 rounded shadow max-h-96 overflow-y-auto">
                    {searchResults.length > 0 ? (
                      searchResults.map((item) => (
                        <Link
                          key={item.variant_id}
                          to={`/details/${item.catalogue}/${item.item_id}`}
                          onClick={() => setSearchText("")}
                          className="flex items-center gap-3 p-2 hover:bg-gray-100"
                        >
                          <img
                            src={`https://backend.nuhababy.com/${item.image}`}
                            className="w-10 h-10 rounded object-cover"
                            alt={item.title}
                          />
                          <div>
                            <p className="text-sm font-medium">{item.title}</p>
                            <p className="text-xs text-gray-500">
                              ৳ {item.flashPrice}
                            </p>
                          </div>
                        </Link>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-gray-500">
                        No product found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="hidden md:flex items-center gap-4 ml-5">
              <Link
                to="/cart"
                className="relative border border-[#fff] md:hover:border md:hover:border-gray-200 rounded-md transition-colors md:hover:bg-black outline-1 outline-gray-500 p-1 hover:text-white"
              >
                <Cart />
                {cartItemCount > 0 && (
                  <span className="absolute -right-2 -top-2 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold leading-5 text-center">
                    {cartItemCount > 99 ? "99+" : cartItemCount}
                  </span>
                )}
                <span className="sr-only">Cart</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Drawer Menu (only for mobile) */}
      <div className="md:hidden">
        <DrawerMenu isOpen={isDrawerOpen} onClose={toggleDrawer} />
      </div>
    </div>
  );
};

export default Navbar;
